import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import {
    Table, TableBody, TableContainer, TableRow, TableHead, Paper, TableCell,
    CircularProgress, Alert, Typography,
    Box,
    TextField,
    // Checkbox,
    LinearProgress,
    Button
} from '@mui/material';
import { getQuenchTeamVideos } from '../../../../../../actions/conservation-burns/conservation-burn-actions';
import { v4 as uuidv4 } from 'uuid';
import useTsPaginator from 'ts-paginator';
import Paginator from '../../../../../layout/Pagination';
import {
    API_CB_VIDEO_MULTIPART_ABORT,
    API_CB_VIDEO_MULTIPART_COMPLETE,
    API_CB_VIDEO_MULTIPART_INIT,
    API_CB_VIDEO_MULTIPART_PART_URL
} from '../../../../../../actions/conservation-burns/constants/conservation-burn-constants';
import { uploadMultipartToS3 } from '../../../../../../utils/s3MultipartUpload';
import axios from 'axios';
import { setAlert } from '../../../../../../actions/alert';

export default function CbPileVariabilityVideos({ projectKey }) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [quenchTeamVideosState, setQuenchTeamVideosState] = useState([]);
    const [uploadUiByRowId, setUploadUiByRowId] = useState({});
    const [error, setError] = useState(null);
    const quenchTeamVideos = useSelector(
        (state) => state.conservationBurnsReducer.quenchTeamVideos
    );
    const tableRecordCount = useSelector(
        (state) => state.conservationBurnsReducer.quenchTeamVideos?.pagination?.total_records
    );

    const {
        totalRecordCount,
        rowsPerPage,
        currentPage,
        _determinePaginationMessage,
        _determinePaginationDisabledState,
        _determineRowsPerPageOptions,
        _handleChangeTotalRecordCount,
        _handleChangeRowsPerPage,
        _handleChangePage,
    } = useTsPaginator(+tableRecordCount || 0, 0);

    const isRowDirty = (row) => {
        const hasNewVideoFile = row.sourceDocument instanceof File;
        const totalChanged =
            String(row.totalPilesQuenched ?? '') !== String(row.initialTotalPilesQuenched ?? '');
        return hasNewVideoFile || totalChanged;
    };

    // Fetch on mount / project change
    useEffect(() => {
        dispatch(getQuenchTeamVideos(projectKey, rowsPerPage, currentPage));
    }, [dispatch, projectKey, rowsPerPage, currentPage]);

    // Turn off loading when data arrives
    useEffect(() => {
        if (quenchTeamVideos) {
            setLoading(false);
        }
    }, [quenchTeamVideos]);

    useEffect(() => {
        _handleChangePage(0);
        _handleChangeRowsPerPage(10);
        _handleChangeTotalRecordCount(+tableRecordCount);
    }, [projectKey, tableRecordCount]);

    // Build local editable state from reducer data
    useEffect(() => {
        const stateArray = quenchTeamVideos?.data?.map((item) => ({
            id: item.id,
            rowId: uuidv4(),
            batchId: item.batchId,
            batch: item.batch,
            totalPilesQuenched: item.totalPilesQuenched ?? '',
            initialTotalPilesQuenched: item.totalPilesQuenched ?? '',
            videoUploadId: item.videoUploadId ?? '',
            sourceDocument: item.sourceDocument ?? '',
            dirty: false,
        })) || [];

        setQuenchTeamVideosState(stateArray);
    }, [quenchTeamVideos]);

    if (loading) return <CircularProgress />;

    if (!quenchTeamVideos?.data?.length) {
        return (
            <Alert severity="info" sx={{ width: 'fit-content' }}>
                No Conservation Burn data for this project
            </Alert>
        );
    }

    const handleFileUpload = async (e, dataId, field) => { //
        const file = e.target.files[0];
        setQuenchTeamVideosState((prevState) =>
            prevState.map((item) => {
                if (item.rowId === dataId) {
                    const nextItem = { ...item, [field]: file };
                    nextItem.dirty = isRowDirty(nextItem);
                    return nextItem;
                }
                return item;
            })
        );
    };

    const startMultipartVideoUpload = async (file, rowId, batchId, videoUploadId, totalPilesQuenched) => {
        const row = quenchTeamVideosState.find((item) => item.rowId === rowId);
        if (!row) return;
        if (!file) return;
        if (!file.type?.startsWith('video/')) {
            setUploadUiByRowId((prev) => ({
                ...prev,
                [rowId]: { state: 'error', progress: 0, error: 'Please select a video file.' },
            }));
            return;
        }
        // Match backend default (2 GiB) unless configured otherwise
        const maxBytes = 2 * 1024 * 1024 * 1024;
        if (file.size > maxBytes) {
            setUploadUiByRowId((prev) => ({
                ...prev,
                [rowId]: { state: 'error', progress: 0, error: 'Video is too large to upload.' },
            }));
            return;
        }

        setUploadUiByRowId((prev) => ({
            ...prev,
            [rowId]: { state: 'preparing', progress: 0, error: null },
        }));

        try {
            const result = await uploadMultipartToS3({
                apiBaseUrl: process.env.REACT_APP_API_URL,
                initPath: `/${API_CB_VIDEO_MULTIPART_INIT}`,
                partUrlPath: `/${API_CB_VIDEO_MULTIPART_PART_URL}`,
                completePath: `/${API_CB_VIDEO_MULTIPART_COMPLETE}`,
                abortPath: `/${API_CB_VIDEO_MULTIPART_ABORT}`,
                projectId: projectKey,
                file,
                extraInitPayload: {
                    batchId: batchId,
                },
                extraCompletePayload: {
                    batchId: batchId,
                    totalPilesQuenched: totalPilesQuenched,
                    videoUploadId: videoUploadId,
                },
                onStateChange: (state) =>
                    setUploadUiByRowId((prev) => ({
                        ...prev,
                        [rowId]: { ...(prev[rowId] || {}), state },
                    })),
                onProgress: (progress) =>
                    setUploadUiByRowId((prev) => ({
                        ...prev,
                        [rowId]: { ...(prev[rowId] || {}), progress },
                    })),
            });

            setQuenchTeamVideosState((prevState) =>
                prevState.map((item) => {
                    if (item.rowId === rowId) {
                        item.sourceDocument = {
                            fileUrl: result.fileUrl,
                            fileName: file.name,
                            key: result.key,
                        };
                        item.dirty = true;
                    }
                    return item;
                })
            );

            setUploadUiByRowId((prev) => ({
                ...prev,
                [rowId]: { state: 'success', progress: 100, error: null },
            }));
        } catch (err) {
            const message =
                err?.response?.data?.detail ||
                err?.message ||
                'Upload failed';
            setUploadUiByRowId((prev) => ({
                ...prev,
                [rowId]: { state: 'error', progress: prev[rowId]?.progress || 0, error: message },
            }));
        }
    };

    const handleInputChange = (e, dataId, field) => {
        setQuenchTeamVideosState((prevState) =>
            prevState.map((item) => {
                if (item.rowId === dataId) {
                    const nextItem = { ...item, [field]: e.target.value };
                    nextItem.dirty = isRowDirty(nextItem);
                    return nextItem;
                }
                return item;
            })
        );
    };

    // const handleCheckboxChange = (e, dataId, field) => {
    //     setQuenchTeamVideosState((prevState) =>
    //         prevState.map((item) => {
    //             if (item.rowId === dataId) {
    //                 item[field] = e.target.checked;
    //                 item.dirty = true;
    //             }
    //             return item;
    //         })
    //     );
    // };

    const handleSubmit = async () => {
        // what we want here is to extract the dirty rows (but there should only be one row, since we allow one upload at a time, otherwise lets set an error message)
        if (quenchTeamVideosState.filter((item) => item.dirty).length > 1) {
            setError('Only one video can be uploaded at a time');
            return;
        }
        if (quenchTeamVideosState.filter((item) => item.dirty).length === 0) {
            setError('No video to upload');
            return;
        }
        setError(null);

        const dirtyRow = quenchTeamVideosState.filter((item) => item.dirty)[0];
        const hasVideoFile = dirtyRow.sourceDocument instanceof File;

        const onSubmitSuccess = () => {
            setQuenchTeamVideosState((prevState) =>
                prevState.map((item) => {
                    if (item.rowId === dirtyRow.rowId) {
                        item.dirty = false;
                    }
                    return item;
                })
            );
            dispatch(setAlert('Quench Team Video Data Submitted', 'success'));
            dispatch(getQuenchTeamVideos(projectKey, rowsPerPage, currentPage));
        };

        try {
            if (hasVideoFile) {
                await startMultipartVideoUpload(
                    dirtyRow.sourceDocument,
                    dirtyRow.rowId,
                    dirtyRow.batchId,
                    dirtyRow.videoUploadId,
                    dirtyRow.totalPilesQuenched
                );
                onSubmitSuccess();
                return;
            }

            // Allow saving totalPilesQuenched without requiring a new video upload.
            const token = JSON.parse(localStorage.getItem('token'))?.token;
            await axios.post(
                `${process.env.REACT_APP_API_URL}/${API_CB_VIDEO_MULTIPART_COMPLETE}/${projectKey}`,
                {
                    batchId: dirtyRow.batchId,
                    videoUploadId: dirtyRow.videoUploadId,
                    totalPilesQuenched: dirtyRow.totalPilesQuenched,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                }
            );
            onSubmitSuccess();
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || 'Upload failed');
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant='h5'>Upload Quench Team Video's</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {/* {quenchTeamVideos.data.map((batch, index) => ( */}


                {/* Main Table - Summary */}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Batch ID</TableCell>
                                {/* <TableCell>Video ID</TableCell> */}
                                <TableCell>Video Ref</TableCell>
                                <TableCell>Total Piles Quenched</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {quenchTeamVideosState.map((qtv) => (
                                <TableRow key={qtv.rowId}>
                                    <TableCell>{qtv.batch}</TableCell>
                                    {/* <TableCell>{qtv.videoUploadId}</TableCell> */}
                                    <TableCell>
                                        {qtv.sourceDocument?.fileUrl ? (
                                            <a
                                                href={`${qtv.sourceDocument.fileUrl}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {'Video'}
                                            </a>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <TextField
                                                    name={'sourceDocument'}
                                                    onChange={(e) => {
                                                        // const file = e.target.files?.[0];
                                                        // Keep local state for any future submit logic
                                                        handleFileUpload(e, qtv.rowId, 'sourceDocument');
                                                        // startMultipartVideoUpload(file, qtv.rowId);
                                                    }}
                                                    type="file"
                                                    inputProps={{ accept: 'video/*' }}
                                                />
                                                {/* <Button
                                                    variant='contained'
                                                    sx={{ width: 'fit-content', mt: 2 }}
                                                // onClick={handleAddItem}
                                                >
                                                    Upload
                                                </Button> */}
                                                {(uploadUiByRowId[qtv.rowId]?.state === 'preparing' ||
                                                    uploadUiByRowId[qtv.rowId]?.state === 'uploading') && (
                                                        //                  <LinearProgress
                                                        //     variant="determinate"
                                                        //     value={uploadUiByRowId[qtv.rowId]?.progress || 0}
                                                        // />
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={uploadUiByRowId[qtv.rowId]?.progress || 0}
                                                                sx={{ flex: 1 }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    minWidth: 56,
                                                                    color: 'primary.main',
                                                                    fontWeight: 700,
                                                                    textAlign: 'right',
                                                                }}
                                                            >
                                                                {(uploadUiByRowId[qtv.rowId]?.progress || 0)}%
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                {uploadUiByRowId[qtv.rowId]?.state === 'finalizing' && (
                                                    //   <LinearProgress />
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LinearProgress sx={{ flex: 1 }} />
                                                        <Typography variant="body2" sx={{ minWidth: 56, fontWeight: 700 }}>
                                                            Finalizing...
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {uploadUiByRowId[qtv.rowId]?.state === 'error' && (
                                                    <Typography variant="caption" color="error">
                                                        {uploadUiByRowId[qtv.rowId]?.error}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            disabled={uploadUiByRowId[qtv.rowId]?.state === 'uploading' || uploadUiByRowId[qtv.rowId]?.state === 'finalizing'}
                                            name={'totalPilesQuenched'}
                                            value={qtv.totalPilesQuenched ?? ''}
                                            onChange={(e) => handleInputChange(e, qtv.rowId, 'totalPilesQuenched')}
                                            type="number"
                                        />
                                    </TableCell>
                                    {/* <TableCell>{qtv.createdAt}</TableCell>
                                    <TableCell>{qtv.updatedAt}</TableCell> */}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                    {/* <Button
                        variant='contained'
                        sx={{ width: 'fit-content', mt: 2 }}
                    // onClick={handleAddItem}
                    >
                        Add Entry
                    </Button> */}
                    <Button
                        variant='contained'
                        sx={{ width: 'fit-content', mt: 2 }}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </Box>
                {error ? <Alert severity='error' sx={{ my: 2 }}>{error}</Alert> : null}
                <Paginator
                    totalRecordCount={totalRecordCount}
                    rowsPerPage={rowsPerPage}
                    currentPage={currentPage}
                    handleChangePage={_handleChangePage}
                    handleChangeRowsPerPage={_handleChangeRowsPerPage}
                    determinePaginationMessage={_determinePaginationMessage}
                    determinePaginationDisabledState={_determinePaginationDisabledState}
                    determineRowsPerPageOptions={_determineRowsPerPageOptions}
                />
            </Box>
        </Box>
    )
}